import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Radio, Music, Users, DollarSign, Clock, Loader2, Send, MessageCircle, Play, Plus, Zap, StopCircle, Wifi } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth } from "../App";
import { toast } from "sonner";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace("https://", "wss://").replace("http://", "ws://");

export default function SessionsPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("live");
  const [createForm, setCreateForm] = useState({ title: "", description: "", genre: "" });

  const headers = (() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  })();

  const fetchSessions = useCallback(() => {
    axios.get(`${API}/api/sessions/`, { params: { status: filter || undefined } })
      .then(res => setSessions(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in first"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/sessions/`, createForm, { headers });
      toast.success("You're LIVE!");
      setShowCreate(false);
      setCreateForm({ title: "", description: "", genre: "" });
      fetchSessions();
      setActiveSession(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
    finally { setCreating(false); }
  };

  const handleEndSession = async (sessionId) => {
    try {
      await axios.post(`${API}/api/sessions/${sessionId}/end`, {}, { headers });
      toast.success("Session ended");
      setActiveSession(null);
      fetchSessions();
    } catch (e) { toast.error("Failed to end session"); }
  };

  const openSession = async (s) => {
    try {
      const res = await axios.get(`${API}/api/sessions/${s.session_id}`);
      setActiveSession(res.data);
    } catch { setActiveSession(s); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="sessions-page">
      <SEO title="Live Sessions" description="Watch producers create beats in real-time. Chat and tip." />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-red-500/20 text-red-400 border-0 mb-3">
            <Wifi className="w-3 h-3 mr-1" /> Live
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-3">
            Live <span className="text-red-400">Sessions</span>
          </h1>
          <p className="text-zinc-500 max-w-lg mx-auto text-sm">
            Watch producers cook beats in real-time. Chat, vibe, and tip your favorites.
          </p>
          <div className="flex justify-center gap-3 mt-5">
            {["live", "ended"].map(f => (
              <Button key={f} variant={filter === f ? "default" : "outline"} size="sm"
                onClick={() => { setFilter(f); setLoading(true); }}
                className={filter === f ? (f === "live" ? "bg-red-500 text-white" : "bg-zinc-700 text-white") : "border-white/10"}
                data-testid={`filter-${f}`}
              >
                {f === "live" && <Wifi className="w-3 h-3 mr-1" />}
                {f === "live" ? "Live Now" : "Past Sessions"}
              </Button>
            ))}
            {user && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild>
                  <Button className="bg-red-500 hover:bg-red-600 text-white font-bold" data-testid="go-live-btn">
                    <Radio className="w-4 h-4 mr-2" /> Go Live
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
                  <DialogHeader><DialogTitle className="font-heading">Start Live Session</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <Label>Session Title</Label>
                      <Input value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                        className="bg-black/50 border-white/10" required placeholder="Making a banger..." data-testid="session-title-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                        className="bg-black/50 border-white/10" rows={2} placeholder="What are you cooking today?" data-testid="session-desc-input" />
                    </div>
                    <div>
                      <Label>Genre</Label>
                      <Input value={createForm.genre} onChange={e => setCreateForm({ ...createForm, genre: e.target.value })}
                        className="bg-black/50 border-white/10" placeholder="Trap, Lo-Fi, R&B..." data-testid="session-genre-input" />
                    </div>
                    <Button type="submit" disabled={creating} className="w-full bg-red-500 text-white font-bold" data-testid="start-session-btn">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Radio className="w-4 h-4 mr-2" />}
                      Go Live
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Main Layout */}
        {activeSession ? (
          <SessionView session={activeSession} user={user} headers={headers}
            onEnd={() => handleEndSession(activeSession.session_id)}
            onBack={() => setActiveSession(null)}
          />
        ) : (
          <div>
            {sessions.length === 0 ? (
              <div className="glass border border-white/10 rounded-xl p-16 text-center max-w-md mx-auto">
                <Radio className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 mb-2">{filter === "live" ? "No live sessions right now" : "No past sessions"}</p>
                {filter === "live" && user && <p className="text-zinc-600 text-xs">Be the first to go live!</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sessions.map(s => (
                  <motion.button key={s.session_id}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    onClick={() => openSession(s)}
                    className="text-left w-full glass border border-white/10 rounded-xl p-5 hover:bg-white/5 transition"
                    data-testid={`session-card-${s.session_id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      {s.status === "live" ? (
                        <Badge className="bg-red-500/20 text-red-400 border-0 text-[10px] animate-pulse">
                          <Wifi className="w-2.5 h-2.5 mr-1" /> LIVE
                        </Badge>
                      ) : (
                        <Badge className="bg-zinc-700 text-zinc-400 border-0 text-[10px]">ENDED</Badge>
                      )}
                      {s.genre && <span className="text-[10px] text-zinc-500">{s.genre}</span>}
                    </div>
                    <h3 className="font-medium text-white text-sm mb-1">{s.title}</h3>
                    <p className="text-[11px] text-zinc-500 mb-3">by {s.producer_name}</p>
                    <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.viewer_count} watching</span>
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{s.tip_count} tips</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SessionView({ session, user, headers, onEnd, onBack }) {
  const [messages, setMessages] = useState(session.messages || []);
  const [chatInput, setChatInput] = useState("");
  const [tipAmount, setTipAmount] = useState(5);
  const [tipMsg, setTipMsg] = useState("");
  const [showTip, setShowTip] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [sending, setSending] = useState(false);
  const [viewers, setViewers] = useState(session.viewer_count || 0);
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);

  const isProducer = user?.user_id === session.producer_id;
  const isLive = session.status === "live";

  // WebSocket connection
  useEffect(() => {
    if (!isLive) return;
    const ws = new WebSocket(`${WS_URL}/api/sessions/ws/${session.session_id}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "chat") {
        setMessages(prev => [...prev, data.message]);
      } else if (data.type === "viewer_count") {
        setViewers(data.count);
      } else if (data.type === "session_ended") {
        toast.info("Session ended");
      }
    };

    return () => { ws.close(); };
  }, [session.session_id, isLive]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !user) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/sessions/${session.session_id}/chat`, { message: chatInput }, { headers });
      setChatInput("");
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const sendTip = async () => {
    if (!user) { toast.error("Sign in to tip"); return; }
    setTipping(true);
    try {
      await axios.post(`${API}/api/sessions/${session.session_id}/tip`, {
        amount: tipAmount, message: tipMsg
      }, { headers });
      toast.success(`Tipped $${tipAmount}!`);
      setShowTip(false);
      setTipMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to tip"); }
    finally { setTipping(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Main Session Area */}
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-zinc-400">Back</Button>
          {isLive && (
            <Badge className="bg-red-500/20 text-red-400 border-0 animate-pulse">
              <Wifi className="w-2.5 h-2.5 mr-1" /> LIVE
            </Badge>
          )}
        </div>

        <div className="glass border border-white/10 rounded-xl p-6" data-testid="session-main">
          <h2 className="font-heading text-xl uppercase mb-1" data-testid="session-title">{session.title}</h2>
          <p className="text-xs text-zinc-500 mb-3">by {session.producer_name} {session.genre && `- ${session.genre}`}</p>
          {session.description && <p className="text-sm text-zinc-300 mb-4">{session.description}</p>}

          {/* Stats Bar */}
          <div className="flex items-center gap-4 text-xs text-zinc-400">
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{viewers} watching</span>
            <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />${session.total_tips || 0} tipped</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{new Date(session.created_at).toLocaleTimeString()}</span>
          </div>

          {/* Producer Controls */}
          {isProducer && isLive && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <Button onClick={onEnd} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="end-session-btn">
                <StopCircle className="w-4 h-4 mr-2" /> End Session
              </Button>
            </div>
          )}
        </div>

        {/* Beat being worked on - visual placeholder */}
        <div className="glass border border-white/10 rounded-xl p-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center mb-4 animate-pulse">
            <Music className="w-10 h-10 text-red-400" />
          </div>
          <p className="text-zinc-400 text-sm">{isLive ? "Producer is cooking..." : "Session has ended"}</p>
          <p className="text-zinc-600 text-xs mt-1">Beat-making in progress</p>
        </div>
      </div>

      {/* Chat + Tips Sidebar */}
      <div className="space-y-4">
        {/* Tip Button */}
        {isLive && user && !isProducer && (
          <Dialog open={showTip} onOpenChange={setShowTip}>
            <DialogTrigger asChild>
              <Button className="w-full bg-neon-yellow hover:bg-neon-yellow/80 text-black font-bold" data-testid="tip-btn">
                <DollarSign className="w-4 h-4 mr-2" /> Send Tip
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 max-w-sm">
              <DialogHeader><DialogTitle className="font-heading">Send a Tip</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-4 gap-2">
                  {[5, 10, 25, 50].map(amt => (
                    <button key={amt}
                      onClick={() => setTipAmount(amt)}
                      className={`p-2 rounded-lg text-center transition ${tipAmount === amt ? "bg-neon-yellow text-black font-bold" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}
                      data-testid={`tip-amount-${amt}`}
                    >${amt}</button>
                  ))}
                </div>
                <Input value={tipMsg} onChange={e => setTipMsg(e.target.value)}
                  placeholder="Say something nice..." className="bg-black/50 border-white/10" data-testid="tip-message-input" />
                <Button onClick={sendTip} disabled={tipping} className="w-full bg-neon-yellow text-black font-bold" data-testid="confirm-tip-btn">
                  {tipping ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  Tip ${tipAmount}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Chat */}
        <div className="glass border border-white/10 rounded-xl overflow-hidden flex flex-col" style={{ height: "480px" }} data-testid="session-chat">
          <div className="p-3 border-b border-white/10 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-red-400" />
            <span className="text-xs font-bold text-white uppercase tracking-wider">Live Chat</span>
            <span className="text-[10px] text-zinc-500 ml-auto">{messages.length} messages</span>
          </div>

          <ScrollArea className="flex-1 p-3">
            <div className="space-y-2">
              {messages.length === 0 && (
                <p className="text-zinc-600 text-xs text-center py-8">No messages yet. Say something!</p>
              )}
              {messages.map((m, i) => (
                <div key={m.message_id || i}
                  className={`text-xs ${m.type === "tip" ? "bg-neon-yellow/10 border border-neon-yellow/20 rounded-lg p-2" : ""}`}
                >
                  <span className={`font-bold ${m.type === "tip" ? "text-neon-yellow" : "text-neon-cyan"}`}>
                    {m.user_name}
                  </span>
                  {m.type === "tip" && <Zap className="w-3 h-3 inline text-neon-yellow ml-1" />}
                  <span className="text-zinc-300 ml-1.5">{m.message}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          {/* Chat Input */}
          {isLive && user && (
            <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex gap-2">
              <Input value={chatInput} onChange={e => setChatInput(e.target.value)}
                placeholder="Type a message..." className="bg-black/50 border-white/10 h-9 text-xs flex-1"
                data-testid="chat-input" />
              <Button type="submit" size="icon" disabled={sending || !chatInput.trim()}
                className="bg-red-500 text-white h-9 w-9" data-testid="chat-send-btn"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
